import { renderToString as katexRenderToString } from 'katex';
import 'katex/dist/katex.min.css';
import { LitElement, PropertyValues, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import showdown from 'showdown';
import tpcQuestionApi from '../redux/api/questionApiSlice';
import { appStateSlice } from '../redux/appStateSlice';
import { AppDispatch, store } from '../redux/store';
import { ReduxController } from '../redux/utils/ReduxController';
import { Question, QuestionListResponse } from '../types';
import { TPCRouter } from '../utils/router';
import { AuthController } from '../../user-area/controllers/auth-controller';
import { SubscriptionController } from '../../user-area/controllers/subscription-controller';
import { trackClickEvent } from '../../events/clicks-tracker';
import { checkMustShowFormId, JOTFORM_IDS, showJotForm } from '../../utils/showJotForm';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

const converter = new showdown.Converter({});
converter.setOption('simpleLineBreaks', true);
converter.setOption('smartIndentationFix', true);

@customElement('tpc-corrector-question-view')
export class TPCQuestionView extends LitElement {
    createRenderRoot() {
        return this;
    }

    dispatch = store.dispatch as AppDispatch;
    rxController = new ReduxController(this);
    get store() {
        return this.rxController.state;
    }

    authController = new AuthController(this);
    subscriptionController = new SubscriptionController(this);

    get user() {
        return this.authController.user;
    }

    get isPremiumUser() {
        return this.user?.isPremium || this.subscriptionController.isPremiumActive;
    }

    @state()
    question: Question | undefined;

    @property({ type: String })
    questionUuid: string | undefined;

    @property({ type: Boolean })
    isWidget = false;

    @property({ type: Boolean })
    isLoadingPlaceHolder = false;

    @state()
    feedbackActive = false;
    @state()
    reportActive = false;

    @state()
    isLoading = false;
    @state()
    error = false;

    @state()
    questionWasOpened = false;

    @state()
    questionShouldBeCollapsed = false;

    async fetchQuestion() {
        this.isLoading = true;
        this.error = false;
        try {
            let question: Question | undefined = undefined;

            const initialHistoryData = this.store.appState.initialHistoryData;
            if (initialHistoryData) {
                question = initialHistoryData.data.questions.find(
                    (question) => question.uuid == this.questionUuid
                );
            } else {
                const storeData = this.store.tpcQuestionApi.queries.getPastCorrections
                    ?.data as QuestionListResponse;
                question = storeData?.data?.questions.find(
                    (question) => question.uuid == this.questionUuid
                );
            }

            if (question) {
                this.question = question;
                this.isLoading = false;
                return;
            } else {
                const { data, isError } = await this.dispatch(
                    tpcQuestionApi.endpoints.getQuestionByUuid.initiate({
                        question_uuid: this.questionUuid ?? '-1',
                        isTutor: this.store.appState.isTutor,
                    })
                );
                if (isError) {
                    console.error('error', isError);
                    this.error = true;
                    return;
                } else {
                    const question = data?.data?.question;
                    if (question) {
                        this.question = question;
                        this.isLoading = false;
                        return;
                    } else {
                        this.error = true;
                        return;
                    }
                }
            }
        } catch (error) {
            console.error('error', error);
            this.error = true;
        }
    }

    loadMath(inputText: string) {
        let text = inputText;
        const inlineRegex = /\\\((.*?)\\\)/g; // Regex for inline LaTeX expressions
        //const inlineRegex = /(?<!\w)\$(.+?)\$(?!\w)|\\\((.*?)\\\)/gs;
        const displayRegex = /\\\[(.*?)\\\]/gs; // Regex for display LaTeX expressions

        const inlineMatches = [...text.matchAll(inlineRegex)].map((match) => match[0]);
        const displayMatches = [...text.matchAll(displayRegex)].map((match) => match[0]);

        text = text.replace(inlineRegex, (match, content) => {
            //escape latex special chars
            let escapedContent = content;
            //.replace(/\$/g, '\\$');
            try {
                return katexRenderToString(escapedContent, {
                    throwOnError: true,
                    output: 'html',
                });
            } catch (error) {
                console.error('error', error);
                return katexRenderToString(escapedContent, {
                    throwOnError: false,
                    errorColor: '#444444',
                    output: 'html',
                });
            }
        });

        text = text.replace(displayRegex, (match, content) => {
            //escape latex special chars
            let escapedContent = content.replace(/\$/g, '\\$');
            try {
                return (
                    `<div class="math-display">` +
                    katexRenderToString(escapedContent, {
                        throwOnError: true,
                        displayMode: true,
                        output: 'html',
                    }) +
                    `</div>`
                );
            } catch (error) {
                console.error('error', error);
                return (
                    `<div class="math-display">` +
                    katexRenderToString(escapedContent, {
                        throwOnError: false,
                        errorColor: '#444444',
                        displayMode: true,
                        output: 'html',
                    }) +
                    `</div>`
                );
            }
        });
        return text;
    }

    checkMustShowForm() {
        if (checkMustShowFormId(JOTFORM_IDS.AJUDANTE_TPC)) {
            showJotForm(JOTFORM_IDS.AJUDANTE_TPC, this.user?.id?.toString() ?? '', this.user?.email ?? '');
        }
    }

    firstUpdated() {
        if (!this.isLoadingPlaceHolder && !this.question) {
            this.fetchQuestion();
        }
        this.checkMustShowForm();
        this.dispatch(appStateSlice.actions.setSelectedQuestionUuid(this.questionUuid));
    }

    askNewQuestion() {
        if (this.isWidget) {
            this.dispatchEvent(new CustomEvent('askNewQuestion'));
        } else {
            window.scrollTo({
                top: 0,
                behavior: 'smooth',
            });
            TPCRouter.getInstance().goToNewExercisePage();
        }
    }

    updated(changedProperties: PropertyValues) {
        if (changedProperties.has('question') && this.question) {
            if (this.question.image) {
                this.questionShouldBeCollapsed = true;
            }
            if (this.question.question) {
                const questionElement = document.querySelector(
                    '.tpc-corrector-question-view-content--question .markdown-content'
                ) as HTMLElement;
                if (questionElement && this.question.question) {
                    const height = questionElement.clientHeight;
                    const windowWidth = window.innerWidth;
                    const mustRenderCollapsed =
                        (height > 170 && windowWidth >= 768) || (height > 120 && windowWidth < 768);
                    if (mustRenderCollapsed) {
                        this.questionShouldBeCollapsed = true;
                    }
                }
            }
        }
    }

    renderLoadingText(lines: number = 3) {
        return html`<div class="tpc-corrector-question-view-content-loading">
                ${Array.from({ length: lines }).map(
                    (_, index) => html`<div
                        class="tpc-corrector-question-view-content-loading-text skeleton"
                        style="width: ${index === 0 ? '60%' : '100%'};"
                    ></div>`
                )}
            </div>
        </div>`;
    }

    handleFeedbackActive(event: CustomEvent) {
        this.feedbackActive = event.detail.active;
    }

    handleReportActive(event: CustomEvent) {
        this.reportActive = event.detail.active;
    }

    copyText(element: Element, textToCopy: string) {
        // copy text to clipboard
        navigator.clipboard.writeText(textToCopy);
        element.classList.add('copied');
        setTimeout(() => {
            if (element) {
                element.classList.remove('copied');
            }
        }, 1500);
    }

    renderSolutionActions() {
        return html`
            <div class="tpc-corrector-question-view-content-actions">
                <div class="tpc-corrector-question-view-content-actions-top">
                    <div class="tpc-corrector-question-view-content-actions-top-left">
                        <tpc-corrector-question-view-feedback
                            .questionUuid=${this.questionUuid}
                            @feedbackActive=${this.handleFeedbackActive}
                        ></tpc-corrector-question-view-feedback>
                        ${this.feedbackActive
                            ? nothing
                            : html`<tpc-corrector-question-view-report
                                  .questionUuid=${this.questionUuid}
                                  @reportActive=${this.handleReportActive}
                              ></tpc-corrector-question-view-report>`}
                    </div>
                    ${!this.reportActive
                        ? html`<div class="tpc-corrector-question-view-content-actions-copy">
                              <button
                                  class="ds-button copy-button"
                                  @click="${(event) => {
                                      this.copyText(
                                          event.target as Element,
                                          this.question?.explanation ?? ''
                                      );
                                  }}"
                              >
                                  <span class="icon icon-078-copy-outline"></span>
                                  <span class="text-not-copied"> Copiar </span>
                                  <span class="icon icon-025-circular-check"></span>
                                  <span class="text-copied"> Copiado </span>
                              </button>
                          </div>`
                        : nothing}
                </div>
                <div class="tpc-corrector-question-view-content-actions-bottom">
                    <span class="icon icon-054-alert-outline"></span>
                    <div>
                        <b>Atenção:</b> Nossa IA é avançada, mas pode gerar respostas imprecisas ou
                        incompletas.
                    </div>
                </div>
            </div>
        `;
    }

    renderTmplusCard() {
        if (this.isPremiumUser || this.isLoading || this.isLoadingPlaceHolder || this.error || this.isWidget) {
            return nothing;
        }
        return html`
            <div class="tpc-corrector-question-view-tmplus-card-container">
                <div class="tpc-corrector-question-view-tmplus-card">
                    <div class="tpc-corrector-question-view-tmplus-card-bg"></div>
                    <div class="tpc-corrector-question-view-tmplus-card-content">
                        <div class="tpc-corrector-question-view-tmplus-card-title">
                        Ainda tem dúvidas?
                        </div>
                        <div class="tpc-corrector-question-view-tmplus-card-description">
                            Assine por menos de R$1 por dia e faça quantas perguntas quiser com registro completo do histórico
                        </div>

                        <a
                            href="/tmplus/"
                            class="ds-button ds-button--tmplus--invert"
                            @click="${() => {
                                trackClickEvent('cta-tmplus-assistant-solution');
                            }}"
                            >Quero mais
                        </a>
                        <div class="tpc-corrector-question-view-tmplus-card-safe">
                            <span class="icon icon-061-unlock"></span>Compra 100% segura
                        </div>
                    </div>
                </div>
            </div>
        `;
    }
    render() {
        return html`<div class="tpc-corrector-question-view-container">
                <div
                    class="tpc-corrector-question-view-title tpc-corrector-question-view-title--question"
                >
                    <span class="icon icon-050-doc-write"></span>
                    Sua questão
                </div>
                <div
                    class="${classMap({
                        'tpc-corrector-question-view-content-wrapper': true,
                        'tpc-corrector-question-view-content-wrapper--question': true,
                        'tpc-corrector-question-view-content-wrapper--question--collapsed':
                            this.questionShouldBeCollapsed && !this.questionWasOpened,
                    })}"
                >
                    <div
                        class="tpc-corrector-question-view-content tpc-corrector-question-view-content--question"
                    >
                        ${
                            this.question?.image
                                ? html`<img
                                      class="tpc-corrector-question-view-content-image"
                                      src="${this.question.image}"
                                      alt="Imagem da questão"
                                  />`
                                : this.question?.question
                                ? html`<div class="markdown-content">
                                      ${this.question?.question ? unsafeHTML(converter.makeHtml(this.loadMath(this.question.question))) : ''}
                                  </div>`
                                : this.isLoading || this.isLoadingPlaceHolder
                                ? this.renderLoadingText(3)
                                : nothing
                        }
                    </div>
                    ${
                        this.questionShouldBeCollapsed && !this.questionWasOpened
                            ? html`
                                  <div
                                      class="tpc-corrector-question-view-content-wrapper--question--collapsed--open"
                                  >
                                      <button
                                          class="ds-button ds-button--light-gray"
                                          @click=${() => (this.questionWasOpened = true)}
                                      >
                                          Ver questão completa
                                      </button>
                                  </div>
                              `
                            : nothing
                    }
                </div>
                <div
                    class="tpc-corrector-question-view-title tpc-corrector-question-view-title--question"
                >
                    <span class="icon icon-051-doc-check"></span>
                    Aprenda com a resposta
                </div>
                <div
                    class="tpc-corrector-question-view-content-wrapper
                tpc-corrector-question-view-content-wrapper--answer"
                >
                    <div class="tpc-corrector-question-view-content">
                        <div class="tpc-corrector-question-view-content-title">Solução</div>
                        <div class="tpc-corrector-question-view-content--answer">
                            ${
                                this.isLoading || this.isLoadingPlaceHolder
                                    ? this.renderLoadingText()
                                    : html`
                                          <div class="markdown-content">
                                              ${this.question?.answer ? unsafeHTML(converter.makeHtml(this.loadMath(this.question.answer))) : ''}
                                          </div>
                                          <button
                                              class="ds-button copy-button"
                                              @click="${(event) => {
                                                  this.copyText(
                                                      event.target as Element,
                                                      this.question?.answer ?? ''
                                                  );
                                              }}"
                                          >
                                              <span class="icon icon-078-copy-outline"></span>
                                              <span class="text-not-copied"> Copiar </span>
                                              <span class="icon icon-025-circular-check"></span>
                                              <span class="text-copied"> Copiado </span>
                                          </button>
                                      `
                            }
                        </div>
                        <div class="spacer"></div>
                        <div class="tpc-corrector-question-view-content-title">Explicação</div>
                        <div class="tpc-corrector-question-view-content--explanation">
                            ${
                                this.isLoading || this.isLoadingPlaceHolder
                                    ? this.renderLoadingText(5)
                                    : html`
                                          <div class="markdown-content">
                                              ${this.question?.explanation ? unsafeHTML(converter.makeHtml(this.loadMath(this.question.explanation))) : ''}
                                          </div>
                                          ${this.renderSolutionActions()}
                                      `
                            }
                        </div>
                    </div>
                </div>
                ${
                    !this.isLoadingPlaceHolder
                        ? html` <div class="tpc-corrector-question-view-footer">
                              <button
                                  class="ds-button ds-button--primary"
                                  @click=${() => {
                                      trackClickEvent('cta-assistant-widget-novaquestao');
                                      this.askNewQuestion();
                                  }}
                              >
                                  <span class="icon icon-050-doc-write"></span>
                                  Nova questão
                              </button>
                          </div>`
                        : nothing
                }
                </div>
                ${this.renderTmplusCard()}
            </div> ${
                this.error
                    ? html`
                          <tm-tpc-corrector-error-modal
                              @tryAgain=${this.fetchQuestion}
                              @newQuestion=${this.askNewQuestion}
                          ></tm-tpc-corrector-error-modal>
                      `
                    : nothing
            }
        `;
    }
}
